import {Link} from 'react-router-dom';
import {CipxInterestForm} from 'forms/CipxInterest';
import styled from 'styled-components';
import {VisuallyHidden} from '../a11y/VisuallyHidden';
import {CodeInPlaceLink} from '../CodeInPlaceLink';

/**
 * A person featured in the PAI interest hero.
 */
interface FeaturedPerson {
  /** Display name, shown under the photo — it is the link's accessible name (the photo itself has an empty alt). */
  name: string;
  /** Absolute path to the photo from the public root (files live in public/people/). */
  img: string;
  /** Personal website the photo and name link to (opens in a new tab). */
  href: string;
  /**
   * Zoom factor applied to the photo inside its frame. 1 = no zoom. Use to crop
   * in tighter on a face. Defaults to 1 when omitted.
   */
  zoom?: number;
  /**
   * CSS `object-position` for the photo inside its frame (e.g. '35% 30%' to
   * shift the visible window left/up). Defaults to 'center' when omitted.
   */
  objectPosition?: string;
}

/**
 * People featured in the PAI interest hero. Tweak `zoom`/`objectPosition` per
 * person to frame each face nicely. Photos live in webapp's `public/people/`
 * (PAI's Vite `publicDir` points there — see `vite.config.ts`).
 */
const FEATURED_PEOPLE: FeaturedPerson[] = [
  {
    name: 'Chris Piech',
    img: '/people/chris.jpg',
    href: 'https://stanford.edu/~cpiech/bio/index.html',
    zoom: 1,
    objectPosition: 'center',
  },
  {
    name: 'Mehran Sahami',
    img: '/people/mehran.jpg',
    href: 'https://profiles.stanford.edu/mehran-sahami',
    zoom: 1,
    objectPosition: 'center',
  },
];

/**
 * Interest landing page for the upcoming PAI (Probability for AI) course.
 *
 * A PAI-flavoured twin of webapp's `/cipx/intro` page: same layout, spacing,
 * and interest-form card, but with PAI copy and Chris Piech and Mehran Sahami
 * as the featured instructors. Body text uses the system font (set explicitly
 * on the page wrapper, overriding PAI's global Source Sans 3 theme — see
 * `paiTheme.ts`), while the page title and nav brand use Jost to match the
 * splash hero title.
 *
 * The top nav reuses the same `container-fluid container-figure` wrapper as the
 * main landing page so the brand sits in the exact same position, but shows a
 * PAI {@link Brand} ("Stanford | PAI") in place of the shared
 * `CodeInPlaceLogo`.
 *
 * Layout is a CSS grid that reflows by breakpoint (a flat DOM kept in one place,
 * rearranged via grid-template-areas):
 * - Wide (above `COLLAPSE_BP`): course text + instructor photos in a left
 *   column, interest form on the right.
 * - Compact (at or below `COLLAPSE_BP`; phones and tablets): a single
 *   left-aligned column — course title (the blurb is hidden so the form stays
 *   above the fold), then the form, then a "Led by:" label with smaller
 *   side-by-side instructor photos.
 *
 * @return The rendered PAI interest landing page.
 */
export const PaiInterestPage = () => {
  return (
    <PageOuter>
      {/* header/main give the page its banner and main landmarks — this page
          renders standalone (no surrounding app chrome provides them). */}
      <NavOuter as="header">
        <div className="container-fluid container-figure w-100">
          <NavInner>
            <NavLeft>
              <Brand />
            </NavLeft>
          </NavInner>
        </div>
      </NavOuter>

      <Hero as="main">
        <div className="container-fluid container-figure w-100">
          <HeroInner>
            <TextBlock>
              <Title>Probability for AI</Title>
              <Description>
                Probability for AI is a new, free Stanford course on the
                probability and mathematics behind modern AI — taught the{' '}
                <CodeInPlaceLink /> way, with a global community of learners and
                volunteer section leaders. Drop your email and we&apos;ll let
                you know when applications open.
              </Description>
            </TextBlock>

            <FormArea>
              <CipxInterestForm
                campaignCollection="generalInterest"
                campaign="pai"
                campaignName="Probability for AI"
                collapseBreakpointPx={COLLAPSE_BP}
              />
            </FormArea>

            <PeopleBlock>
              <LedBy>Led by:</LedBy>
              <PhotoRow>
                {FEATURED_PEOPLE.map(person => (
                  <PersonPhoto key={person.name} person={person} />
                ))}
              </PhotoRow>
            </PeopleBlock>
          </HeroInner>
        </div>
      </Hero>
    </PageOuter>
  );
};

/**
 * Single instructor photo (optionally zoomed) with the person's name below it.
 * The whole photo + name links to the person's website.
 *
 * @param person - The featured person to render (name, image, link, framing).
 * @return The rendered, linked photo with the person's name.
 */
const PersonPhoto = ({person}: {person: FeaturedPerson}) => {
  const {name, img, href, zoom = 1, objectPosition = 'center'} = person;
  return (
    <Photo href={href} target="_blank" rel="noreferrer">
      <PhotoFrame>
        {/* Empty alt: the visible name below is the link's label, so a
            duplicate alt would make screen readers announce the name twice. */}
        <img
          src={img}
          alt=""
          style={{transform: `scale(${zoom})`, objectPosition}}
        />
      </PhotoFrame>
      <PhotoName>{name}</PhotoName>
      <VisuallyHidden>(opens in a new tab)</VisuallyHidden>
    </Photo>
  );
};

/**
 * The nav brand for the PAI interest page: "Stanford | PAI", mirroring the
 * shared `CodeInPlaceLogo`'s two-part styling but with PAI copy.
 * Links back to the site root.
 *
 * @return The rendered, linked nav brand.
 */
const Brand = () => {
  return (
    <Link to="/">
      <StanfordTitle>Stanford</StanfordTitle>
      <NavTitle>
        {/* The divider is visual only; without aria-hidden some screen
            readers announce it as "vertical line". */}
        <NavBar aria-hidden="true">|</NavBar> PAI
      </NavTitle>
    </Link>
  );
};

/* The "| PAI" piece renders in Jost (the Futura revival) to match the splash
   hero title — see HeroSplash. Explicit because PageOuter forces the system
   font on the rest of the page. */
const NavTitle = styled.span`
  font-family: 'Jost', sans-serif;
  font-size: 30px;
  font-weight: 700;
  margin: 0;
  color: black;
`;

/* Nunito's "|" glyph sits low against the Stanford wordmark; nudge it up so
   the divider reads vertically centered in the lockup. */
const NavBar = styled.span`
  display: inline-block;
  transform: translateY(-3px);
`;

const StanfordTitle = styled.span`
  display: inline-block;
  font-family:
    Stanford, 'Source Serif Pro', Georgia, Times, 'Times New Roman', serif;
  font-style: normal;
  font-weight: 400;
  text-decoration: none;
  transform: translateZ(0);
  letter-spacing: 0;
  font-feature-settings: 'liga';
  font-variant-ligatures: discretionary-ligatures;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #8c1515;
  font-size: 36px;
  line-height: 40px;
  vertical-align: bottom;
  margin-right: 10px;
  padding-right: 5px;
  background-color: white;
  border-radius: 5px;
  padding-top: 4px;
  padding-bottom: 4px;
`;

/* The native system UI font. Set explicitly because PAI's global course theme
   otherwise applies Source Sans 3 to body + headings (see paiTheme.ts); this
   page is meant to read in the platform's default system font. */
const SYSTEM_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/* Viewport width (px) at or below which the page collapses from the
   two-column hero to the single-column compact layout. High enough that
   tablets (e.g. iPad portrait and landscape) get the compact layout — two
   columns are cramped below this. Passed to CipxInterestForm so the card
   chrome drops at the same width. The compact range is the closed interval
   [0, COLLAPSE_BP]. */
const COLLAPSE_BP = 1024;

const PageOuter = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  font-family: ${SYSTEM_FONT};
`;

/* Nav styles mirror SplashNav so the brand lands in the exact same spot. */
const NavOuter = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #fff;
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const NavInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.75rem 0;
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 64px);
  padding: 3rem 0 4rem;

  /* On mobile, don't vertically center within the viewport — start the
     content right under the nav. */
  @media (max-width: ${COLLAPSE_BP}px) {
    align-items: flex-start;
    min-height: 0;
    padding-top: 1.5rem;
  }
`;

const HeroInner = styled.div`
  display: grid;
  width: 100%;
  align-items: center;
  justify-items: center;
  gap: 2rem 4rem;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    'text form'
    'people form';

  /* Compact: single column — text, then the form, then the instructor photos,
     all left-aligned against the container edge rather than centered. Tight
     gap so the form sits close under the text; PeopleBlock adds its own top
     margin to keep the photos a bit separated from the form. */
  @media (max-width: ${COLLAPSE_BP}px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'text'
      'form'
      'people';
    gap: 1rem;
    justify-items: start;
  }
`;

const TextBlock = styled.div`
  grid-area: text;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.5rem;
  max-width: 560px;

  /* Compact: left-align the title. Full container width (no 440px cap like
     the form) so the title only wraps when the viewport actually runs out —
     the shared left edge comes from the grid's justify-items: start. */
  @media (max-width: ${COLLAPSE_BP}px) {
    align-items: flex-start;
    text-align: left;
    width: 100%;
    max-width: none;
  }
`;

const FormArea = styled.div`
  grid-area: form;
  display: flex;
  justify-content: center;
  width: 100%;

  /* Compact: pin the form to the left edge with the title and photos. */
  @media (max-width: ${COLLAPSE_BP}px) {
    justify-content: flex-start;
  }
`;

const PeopleBlock = styled.div`
  grid-area: people;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  /* On mobile, match the form's width and left-align the contents so "Led by:"
     and the photos line up to the same left edge. */
  @media (max-width: ${COLLAPSE_BP}px) {
    align-items: stretch;
    width: 100%;
    max-width: 440px;
    margin-top: 0.75rem;
  }
`;

const LedBy = styled.div`
  /* Mobile-only label above the photos, left-aligned with them. */
  display: none;
  font-size: 1rem;
  font-weight: 600;
  color: #6e6e73;

  @media (max-width: ${COLLAPSE_BP}px) {
    display: block;
    text-align: left;
  }
`;

const PhotoRow = styled.div`
  display: flex;
  gap: 2.5rem;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: ${COLLAPSE_BP}px) {
    gap: 1.25rem;
    flex-wrap: nowrap;
    width: 100%;
  }
`;

const PhotoFrame = styled.div`
  width: 168px;
  height: 168px;
  border-radius: 20px;
  overflow: hidden;
  background-color: #80221c;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* On mobile, photos flex to fill the row (which matches the form width),
     staying square via aspect-ratio. */
  @media (max-width: ${COLLAPSE_BP}px) {
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
  }
`;

const PhotoName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #1d1d1f;

  @media (max-width: ${COLLAPSE_BP}px) {
    font-size: 0.9rem;
  }
`;

const Photo = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;

  &:hover ${PhotoName} {
    color: #8c1515;
    text-decoration: underline;
  }

  /* Each photo takes an equal share of the row width on mobile. */
  @media (max-width: ${COLLAPSE_BP}px) {
    flex: 1;
    min-width: 0;
  }
`;

const Title = styled.h1`
  /* Jost (the Futura revival) to match the splash hero title — see HeroSplash.
     Explicit so PAI's global Source Sans 3 heading theme doesn't override it. */
  font-family: 'Jost', sans-serif;
  font-size: clamp(2.75rem, 6vw, 4rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
`;

const Description = styled.p`
  font-size: 1.3rem;
  line-height: 1.5;
  color: #6e6e73;
  margin: 0;

  /* On mobile the blurb pushes the form below the fold; hide it and lead with
     the title + form. */
  @media (max-width: ${COLLAPSE_BP}px) {
    display: none;
  }
`;
